// src/App.js (updated – no global loading screen + analytics ping)
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// Import Components
import Header from './components/Header';
import ResetPassword from './components/ResetPassword';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import Gallery from './components/Gallery';
import AboutPage from './components/AboutPage';
import SearchResultsPage from './pages/SearchResultsPage';
import MaintenancePage from './components/MaintenancePage';

// Import Dashboards
import EmployerDashboard from './pages/EmployerDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import AdminDashboard from './pages/AdminDashboard';

// Import Modals
import LoginModal from './components/LoginModal';
import RegisterModal from './components/RegisterModal';
import DonateModal from './components/DonateModal';
import RequestModal from './components/RequestModal';

import VerifyEmail from './pages/VerifyEmail';

import TraineeStats from './pages/TraineeStats';
// Import Translation Provider
import { TranslationProvider } from './contexts/TranslationContext';

import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Sitemap from './pages/Sitemap';

// ✅ ADDED: Visitor analytics heartbeat
import useAnalyticsPing from './hooks/useAnalyticsPing';

// Create Search Context
export const SearchContext = createContext();
export const useSearch = () => useContext(SearchContext);

// ProtectedRoute Component
const ProtectedRoute = ({ children, allowedTypes }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (!token || !storedUser) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const userData = JSON.parse(storedUser);
        setUserType(userData.user_type);

        // Verify the user type is allowed
        if (allowedTypes.includes(userData.user_type)) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [allowedTypes]);

  // Show loading state while checking authentication (only for protected routes)
  if (isAuthenticated === null) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>Verifying authentication...</p>
      </div>
    );
  }

  // If not authenticated, redirect to home
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If authenticated but wrong user type, redirect to appropriate dashboard
  if (userType && !allowedTypes.includes(userType)) {
    switch (userType) {
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      case 'manager':
        return <Navigate to="/manager-dashboard" replace />;
      case 'employer':
        return <Navigate to="/employer-dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return children;
};

// DashboardLayout Component (to handle header/footer visibility)
const DashboardLayout = ({ children }) => {
  return <div className="dashboard-layout">{children}</div>;
};

// Set up axios defaults
axios.defaults.baseURL = 'https://hsn.urugowoc.org.rw';

// Axios request interceptor
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add language parameter to all requests
  const language = localStorage.getItem('language') || 'english';
  if (config.params) {
    config.params.lang = language;
  } else {
    config.params = { lang: language };
  }

  return config;
});

// Axios response interceptor for handling token expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

function App() {
  // ✅ ADDED: heartbeat hook — sends /backend/api/analytics-ping every 2 min.
  // Runs for every visitor, even when not logged in. Non-blocking.
  useAnalyticsPing();

  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);

  const hireScrollRef = useRef(null);

  const handleHireClick = () => {
    if (hireScrollRef.current) {
      hireScrollRef.current();
    }
  };

  // Check maintenance mode on app start (non‑blocking – we set state and render anyway)
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await axios.get('/backend/api/maintenance-status');
        setMaintenanceMode(response.data.maintenance_mode);
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
        setMaintenanceMode(false);
      } finally {
        setCheckingMaintenance(false);
      }
    };

    checkMaintenanceMode();
  }, []);

  // Check if user is logged in on app start (non‑blocking)
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (!token || !storedUser) {
        setUser(null);
        return;
      }

      try {
        const userData = JSON.parse(storedUser);

        // Verify token with backend
        axios
          .get('/backend/api/verify-token', {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(() => {
            setUser(userData);
          })
          .catch(() => {
            // Token is invalid or expired
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          });
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    };

    // Only check auth after maintenance check completes (to avoid race)
    if (!checkingMaintenance) {
      checkAuthStatus();
    }

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkingMaintenance]);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setShowLoginModal(false);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const handleRequest = (departmentId) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    setSelectedDepartment(departmentId);
    setShowRequestModal(true);
  };

  // Search function
  const performSearch = async (query, type = 'all') => {
    if (!query || query.trim().length < 2) return null;

    setSearchLoading(true);
    setLastSearchQuery(query);

    try {
      const response = await axios.get('/backend/api/search', {
        params: { q: query, type: type },
      });

      setSearchResults(response.data);

      // Store search results for cross-page access
      localStorage.setItem('lastSearchResults', JSON.stringify(response.data));
      localStorage.setItem('lastSearchQuery', query);

      return response.data;
    } catch (error) {
      console.error('Search error:', error);
      const errorResult = {
        query: query,
        results: {},
        error: 'Search failed. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setSearchResults(errorResult);
      return errorResult;
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear search results
  const clearSearchResults = () => {
    setSearchResults(null);
    setLastSearchQuery('');
    localStorage.removeItem('lastSearchResults');
    localStorage.removeItem('lastSearchQuery');
  };

  // Create Search Context value
  const searchContextValue = {
    searchResults,
    searchLoading,
    lastSearchQuery,
    performSearch,
    clearSearchResults,
    setSearchResults,
  };

  // If maintenance mode is ON and user is NOT logged in, show maintenance page
  if (maintenanceMode && !user) {
    return (
      <TranslationProvider>
        <Router>
          <MaintenancePage onLoginClick={() => setShowLoginModal(true)} />
          {showLoginModal && (
            <LoginModal
              onClose={() => setShowLoginModal(false)}
              onLogin={handleLogin}
              onSwitchToRegister={() => {
                setShowLoginModal(false);
                setShowRegisterModal(true);
              }}
            />
          )}
          {showRegisterModal && (
            <RegisterModal
              onClose={() => setShowRegisterModal(false)}
              onRegister={handleLogin}
              onSwitchToLogin={() => {
                setShowRegisterModal(false);
                setShowLoginModal(true);
              }}
            />
          )}
        </Router>
      </TranslationProvider>
    );
  }

  // Normal render – no global loading screen
  return (
    <TranslationProvider>
      <SearchContext.Provider value={searchContextValue}>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes (with Header & Footer) */}
              <Route
                path="/"
                element={
                  <>
                    <Header
                      user={user}
                      onLogout={handleLogout}
                      onLoginClick={() => setShowLoginModal(true)}
                      onDonateClick={() => setShowDonateModal(true)}
                      onHireClick={handleHireClick}
                    />
                    <HomePage
                      onRequest={handleRequest}
                      onDonateClick={() => setShowDonateModal(true)}
                      onHireClick={hireScrollRef}
                    />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/gallery"
                element={
                  <>
                    <Header
                      user={user}
                      onLogout={handleLogout}
                      onLoginClick={() => setShowLoginModal(true)}
                      onDonateClick={() => setShowDonateModal(true)}
                      onHireClick={handleHireClick}
                    />
                    <Gallery />

                    <Footer />
                  </>
                }
              />
              <Route
                path="/stats"
                element={
                  <>
                    <Header
                      user={user}
                      onLogout={handleLogout}
                      onLoginClick={() => setShowLoginModal(true)}
                      onDonateClick={() => setShowDonateModal(true)}
                      onHireClick={handleHireClick}
                    />
                    <TraineeStats />
                    <Footer />
                  </>
                }
              />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/sitemap" element={<Sitemap />} />

              <Route
                path="/about"
                element={
                  <>
                    <Header
                      user={user}
                      onLogout={handleLogout}
                      onLoginClick={() => setShowLoginModal(true)}
                      onDonateClick={() => setShowDonateModal(true)}
                      onHireClick={handleHireClick}
                    />
                    <AboutPage />
                    <Footer />
                  </>
                }
              />

              <Route
                path="/search"
                element={
                  <>
                    <Header
                      user={user}
                      onLogout={handleLogout}
                      onLoginClick={() => setShowLoginModal(true)}
                      onDonateClick={() => setShowDonateModal(true)}
                      onHireClick={handleHireClick}
                    />
                    <SearchResultsPage />
                    <Footer />
                  </>
                }
              />

              {/* Dashboard Routes (without Header & Footer) */}
              <Route
                path="/employer-dashboard"
                element={
                  <ProtectedRoute allowedTypes={['employer']}>
                    <DashboardLayout>
                      <EmployerDashboard user={user} onLogout={handleLogout} />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/manager-dashboard"
                element={
                  <ProtectedRoute allowedTypes={['manager']}>
                    <DashboardLayout>
                      <ManagerDashboard user={user} onLogout={handleLogout} />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/forgot-password"
                element={<div>Redirect to login modal with forgot password open</div>}
              />
              <Route path="/verify-email" element={<VerifyEmail />} />

              <Route
                path="/admin-dashboard"
                element={
                  <ProtectedRoute allowedTypes={['admin']}>
                    <DashboardLayout>
                      <AdminDashboard user={user} onLogout={handleLogout} />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>

            {/* Modals */}
            {showLoginModal && (
              <LoginModal
                onClose={() => setShowLoginModal(false)}
                onLogin={handleLogin}
                onSwitchToRegister={() => {
                  setShowLoginModal(false);
                  setShowRegisterModal(true);
                }}
              />
            )}

            {showRegisterModal && (
              <RegisterModal
                onClose={() => setShowRegisterModal(false)}
                onRegister={handleLogin}
                onSwitchToLogin={() => {
                  setShowRegisterModal(false);
                  setShowLoginModal(true);
                }}
              />
            )}

            {showDonateModal && (
              <DonateModal
                onClose={() => setShowDonateModal(false)}
                onDonate={() => {
                  setShowDonateModal(false);
                  alert('Thank you for your donation!');
                }}
              />
            )}

            {showRequestModal && (
              <RequestModal
                onClose={() => setShowRequestModal(false)}
                departmentId={selectedDepartment}
                user={user}
              />
            )}
          </div>
        </Router>
      </SearchContext.Provider>
    </TranslationProvider>
  );
}

export default App;